import React from 'react'
import update from 'immutability-helper'
import moment from 'moment'
import { connect } from 'griddle-react'
import {range, isArray, every, chain} from 'lodash'

export const isEmptyOrWhiteSpace = testRegex(/^\s*$/)

export function toDownloadableFileName (name = '') {
  return name.trim().toLocaleLowerCase().replaceAll(/\s+/g,' ').replaceAll(/\s/g,'-')
}

export function encode(text) {
  const encoder = new TextEncoder()
  return encoder.encode(text)
}

export function testRegex(regex) {
  return regex.test.bind(regex)
}

export function dayRange (start, end) {
  const days = Math.abs(moment.duration(moment(end).startOf('day').diff(moment(start).startOf('day'))).as('days')) + 1
  return range(days).map(offset => moment(start).startOf('day').add(offset, 'd').toDate()) 
}

export function isFieldEmpty (field) {
  switch (field.type) {
    case "panel": return every(field.children, field => isFieldEmpty(field))
    case "dropdown": return every(field.value, value => value == "")
    default: return field.value == ""
  }
}

export function pickAttributes (props, prefix) {
  const pattern = new RegExp(`^${prefix}`)
  return chain(props)
    .pickBy((_,key) => pattern.test(key))
    .mapKeys((_,key) => {
      const newKey = key.replace(pattern,'')
      return newKey[0].toLowerCase() + newKey.substring(1)
    })
    .value()
}

export function viewInCycle(date, cycle, view) {
  const viewTests = {
    month: () => moment(cycle.start).startOf('month').startOf('week').isSameOrBefore(moment(date)) && moment(cycle.end).endOf('month').endOf('week').isSameOrAfter(moment(date)),
    week: () => moment(cycle.start).startOf('week').isSameOrBefore(moment(date)) && moment(cycle.end).endOf('week').isSameOrAfter(moment(date)),
    day: () => moment(cycle.start).isSameOrBefore(moment(date)) && moment(cycle.end).isSameOrAfter(moment(date))
  }
  return (viewTests[view] || viewTests.month)()
}

export function isInCycle(date, cycle) {
  return moment(cycle.start).isSameOrBefore(moment(date)) &&
         moment(cycle.end).isSameOrAfter(moment(date))
}

export function get (property, def) {
  return (obj) => obj[property] || def
}

export function isEmpty(str) {
  return (!str || 0 === str.length)
}

export function isUUID(value) {
  return /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/.test(value);
}

export function uuid(a) {
  return a
    ? (a^Math.random()*16>>a/4).toString(16)
    :([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, uuid);
}

export function truncateText(max, overflow = '...') {
  return text => text.length > max
    ? text.substring(0, max - overflow.length) + overflow
    : text
}

export function bind (context, ...methods) {
  methods.forEach((method) => context[method] = context[method].bind(context))
}

export function delay (delay, comp, mapArgs = a => a) {
  let id = 0
  return (...args) => {
    clearTimeout(id) 
    const mappedArgs = mapArgs(args)
    id = isArray(mappedArgs)
      ? setTimeout(comp, delay, ...mappedArgs)
      : setTimeout(comp, delay, mappedArgs) 
  }
}

export const formatNumber = (function (formatter) {
  return formatter.format.bind(formatter)
}(new Intl.NumberFormat('en-US', { maximumSignificantDigits: 3 })))

export const enhancedWithRowData = connect((state, props) => {
  return {
    rowData: rowDataSelector(state, props)
  }
})

function rowDataSelector (state, { griddleKey }) {
  return state
    .get('data')
    .find(rowMap => rowMap.get('griddleKey') === griddleKey)
    .toJSON()
}

export const enhancedWithPageProperties = connect((state, props) => {
  return {
    pageProperties: pagePropertiesSelector(state)
  }
})

function pagePropertiesSelector (state) {
  return state
    .get('pageProperties')
    .toJSON()
}

export const enhancedWithSortColumns = connect((state,props) => ({sortColumns: sortColumnsSelector(state,props)}))

function sortColumnsSelector (state,{sortProperty}) {
  return sortProperty
}

const parser = new DOMParser()

export function htmlToText(html) {
  const doc = parser.parseFromString(html, "text/html")
  const treeWalker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  const nodeList = []
  let currentNode =  treeWalker.nextNode()
  while(currentNode) {
    if (currentNode.textContent != 'undefined')
      nodeList.push(currentNode.textContent)
    currentNode = treeWalker.nextNode()
  }
  return nodeList.join(' ')
}

export function shuffleArray(array = []) {
  return array.reduce((array, element, index, src) => {
    const i = src.length - 1 - index
    const j = Math.floor(Math.random() * (i + 1))
    if (i == j) {
      return array
    } else {
      const a = array[i]
      const b = array[j]
      return update(array, {$splice: [[i,1],[i,0,b],[j,1],[j,0,a]]})
    }
  }, array);
}

export function toDisplayPoints(points = 0, precision = 3) {
  return Number.isSafeInteger(points) ? points : points.toPrecision(precision)
}

export function buildContext (props, state) {
  return {...omit(props, ['children']), ...state}
}

class Ratio {

  constructor({width, height, unit}) {
    this.unit = unit
    this.width = width
    this.height = height
    this.widthRatio = width / height
    this.heightRatio = height / width
  }

  size(width, height) {
    return {width: `${width}${this.unit}`, height: `${height}${this.unit}`, "viewBox": `0 0 ${this.width} ${this.height}`}
  }

  keep(width, height){
    return height
      ? this.size(Math.round(this.widthRatio * height), height)
      : width 
        ? this.size(width, Math.round(this.heightRatio * width))
        : this.size(this.width, this.height)
  }
}

export function createRatio(width, height, unit = 'px') {
  return new Ratio({width, height, unit})
}

export function SvgKeepRatio ({viewBox, width, height, children}) {
  const ratio = createRatio(viewBox.width, viewBox.height)
  return (<svg {...ratio.keep(width, height)}>{children}</svg>)
}